/**
 * Camera QR scanning: the native BarcodeDetector where the browser has one (Chrome on macOS, ChromeOS,
 * Android), otherwise jsQR on video frames. Camera access needs a secure page (localhost or HTTPS).
 */
import jsQR from "jsqr";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

export type QrScannerStatus = "idle" | "starting" | "scanning" | "error";

interface DetectedBarcode {
  rawValue: string;
}

interface BarcodeDetectorLike {
  detect: (source: CanvasImageSource) => Promise<DetectedBarcode[]>;
}

type BarcodeDetectorConstructor = new (options: { formats: string[] }) => BarcodeDetectorLike;

/** Decodes a QR code in an RGBA frame (jsQR); exported for tests. */
export function decodeQrFrame(data: Uint8ClampedArray, width: number, height: number): string | null {
  return jsQR(data, width, height, { inversionAttempts: "dontInvert" })?.data ?? null;
}

export function cameraSupport(): { supported: boolean; reason?: string } {
  if (typeof window !== "undefined" && !window.isSecureContext) {
    return { supported: false, reason: "The camera needs a secure page: open the app on localhost or over HTTPS." };
  }
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { supported: false, reason: "This browser can't use a camera here. Type the code instead." };
  }
  return { supported: true };
}

function nativeDetector(): BarcodeDetectorLike | null {
  const Detector = (globalThis as { BarcodeDetector?: BarcodeDetectorConstructor }).BarcodeDetector;
  if (!Detector) return null;
  try {
    return new Detector({ formats: ["qr_code"] });
  } catch {
    return null;
  }
}

export interface UseQrScanner {
  videoRef: RefObject<HTMLVideoElement | null>;
  status: QrScannerStatus;
  error: string | null;
  start: () => Promise<void>;
  stop: () => void;
}

/** Starts the camera into `videoRef` and calls `onResult` with the first decoded text. */
export function useQrScanner(onResult: (text: string) => void): UseQrScanner {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const frameRef = useRef<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const onResultRef = useRef(onResult);
  const [status, setStatus] = useState<QrScannerStatus>("idle");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    onResultRef.current = onResult;
  });

  const stop = useCallback(() => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
    for (const track of streamRef.current?.getTracks() ?? []) track.stop();
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setStatus((current) => (current === "error" ? current : "idle"));
  }, []);

  const start = useCallback(async () => {
    const support = cameraSupport();
    if (!support.supported) {
      setError(support.reason ?? "The camera is not available");
      setStatus("error");
      return;
    }
    setError(null);
    setStatus("starting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      const video = videoRef.current;
      if (!video) {
        stop();
        return;
      }
      video.srcObject = stream;
      await video.play();
    } catch (cause) {
      const denied = cause instanceof DOMException && cause.name === "NotAllowedError";
      setError(
        denied
          ? "Camera access was refused. Allow the camera in the browser, or type the code."
          : "The camera could not start.",
      );
      setStatus("error");
      return;
    }
    setStatus("scanning");
    const detector = nativeDetector();
    const scan = async () => {
      const video = videoRef.current;
      if (!video || !streamRef.current) return;
      let text: string | null = null;
      if (video.readyState >= video.HAVE_CURRENT_DATA && video.videoWidth > 0) {
        if (detector) {
          const found = await detector.detect(video).catch(() => []);
          text = found[0]?.rawValue ?? null;
        } else {
          const canvas = (canvasRef.current ??= document.createElement("canvas"));
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const context = canvas.getContext("2d", { willReadFrequently: true });
          if (context) {
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const frame = context.getImageData(0, 0, canvas.width, canvas.height);
            text = decodeQrFrame(frame.data, frame.width, frame.height);
          }
        }
      }
      if (text) {
        stop();
        onResultRef.current(text);
        return;
      }
      frameRef.current = requestAnimationFrame(() => void scan());
    };
    frameRef.current = requestAnimationFrame(() => void scan());
  }, [stop]);

  useEffect(() => stop, [stop]);

  return { videoRef, status, error, start, stop };
}
